import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import API_URLS from '../../backend/ApiUrls'
import { IApprovalRequest } from '../../definitions/Approval'
import { useAppDispatch } from '../../store/Hooks'
import { changeRoute } from '../../store/slices/SessionSlice'
import { Cell, Row } from '../controls/table'
import useCommand from '../hooks/useCommand'
import useInvalidate from '../hooks/useInvalidate'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'
import { snackbar } from '../services/SnackbarService'
import ApprovalModal from './ApprovalHubModal'
import ApprovalTable from './ApprovalHubTable'
import useInvalidatePagination from '../hooks/useInvalidatePagination'

const Approvals = () => {
    const [openModal, setOpenModal] = useState(false)
    const [selectedApproval, setSelectedApproval] = useState<IApprovalRequest>(null)
    const invalidateContractListSmartTable = useInvalidatePagination({ queryKey: buildQueryKey(API_URLS.os.getContracts + '/*') })
    const invalidateContractSchemaList = useInvalidatePagination({ queryKey: buildQueryKey(API_URLS.os.getContractSchemas + '/*') })
    const invalidateSchemaVersions = useInvalidate({ queryKey: buildQueryKey('schema-versions/*') })
    const invalidateApprovalHub = useInvalidate({ queryKey: buildQueryKey('get-approvals') })
    const invalidateSchemaData = useInvalidate({ queryKey: buildQueryKey('get-schema/*') })
    const invalidateEntityApprovalStatus = useInvalidate({ queryKey: buildQueryKey('entity-approval/*') })
    const invalidateContractData = useInvalidate({ queryKey: buildQueryKey('get-contract/*') })
    const invalidateContractsVersion = useInvalidate({ queryKey: buildQueryKey('contract-versions/*') })

    const invalidateFlowSettlementApprovals = useInvalidate({ queryKey: buildQueryKey(`get-approval-/*`) })
    const invalidateFlow = useInvalidate({ queryKey: buildQueryKey(`get-flow-/*`) })
    const invalidateFlowVersions = useInvalidate({ queryKey: buildQueryKey(`get-all-version-flows-/*`) })
    const invalidateApprovedEngines = useInvalidate({ queryKey: buildQueryKey(`approvedEngines/*`) })
    const invalidatePendingEngines = useInvalidate({ queryKey: buildQueryKey(`pendingEngines/*`) })
    const invalidateSettlementDetails = useInvalidate({ queryKey: buildQueryKey('settlement-details' + '/*') })
    const invalidateSettlements = useInvalidate({ queryKey: buildQueryKey('settlements' + '/*') })
    const invalidateSettlementsReport = useInvalidate({ queryKey: buildQueryKey('compiledReports-/*') })

    const dispatch = useAppDispatch()

    const { status, response } = useQuery({
        queryKey: buildQueryKey('get-approvals'),
        queryFn: () => ApiClient.getApprovals(),
        cache: false
    })

    const { execute: navigateToContract, status: navigateToContractStatus } = useCommand({
        commandFn: async (item: IApprovalRequest) => {
            const contract = await ApiClient.getContract(item.approvalEntityUUID)
            const contractFiles = await ApiClient.getContractFiles(item.approvalEntityUUID)

            return { contract, contractFiles }
        },
        onFinish: result => {
            dispatch(
                changeRoute({
                    route: 'contract-editor',
                    params: {
                        contractUUID: result.response.contract.uuid,
                        uploadedFiles: result.response.contractFiles ?? []
                    },
                    customRouteName: result.response.contract.name
                })
            )
        }
    })

    const { execute: navigateToSettlement, status: navigateToSettlementStatus } = useCommand({
        commandFn: async (item: IApprovalRequest) => ApiClient.getSettlementDetails(item.approvalEntityUUID),
        onFinish: result => {
            if (result.status === 'success') {
                dispatch(
                    changeRoute({
                        route: 'settlement-details',
                        params: {
                            settlementId: result.response.settlement.uuid,
                            contractId: result.response.settlement.contractId,
                            eeId: result.response.settlement.executionEnvironmentId
                        }
                    })
                )
            }
        }
    })

    const handleManageApproval = (item: IApprovalRequest) => {
        setSelectedApproval(item)
        setOpenModal(true)
    }

    const handleCloseModal = () => {
        setOpenModal(false)
        setSelectedApproval(null)
    }

    const { execute: executeApprove, status: approveStatus } = useCommand({
        commandFn: async (approvalEntityUUID: string) => {
            return await ApiClient.approveRequest(approvalEntityUUID)
        },
        onFinish: result => {
            if (result.status === 'success' && result.response) {
                snackbar.show({
                    severity: 'success',
                    text: `Approval granted for ${selectedApproval?.entityName}`
                })

                invalidateApprovalHub()
                invalidateEntityApprovalStatus()

                if (selectedApproval?.entityType === 'contract') {
                    invalidateContractListSmartTable()
                    invalidateContractData()
                    invalidateContractsVersion()
                }
                if (selectedApproval?.entityType === 'schema') {
                    invalidateContractSchemaList()
                    invalidateSchemaData()
                    invalidateSchemaVersions()
                }
                if (selectedApproval?.entityType === 'flow') {
                    invalidateFlowSettlementApprovals()
                    invalidateFlowVersions()
                    invalidateFlow()
                    invalidateApprovedEngines()
                    invalidatePendingEngines()
                }
                if (selectedApproval?.entityType === 'settlement') {
                    invalidateSettlementsReport()
                    invalidateFlowSettlementApprovals()
                    invalidateSettlementDetails()
                    invalidateSettlements()
                }

                handleCloseModal()
            } else if (result.status === 'error') {
                console.error('Error approving request:', result.errorText)
                snackbar.show({
                    severity: 'error',
                    text: 'Failed to approve request: ' + result.errorText
                })
            }
        }
    })

    const handleApprove = async () => {
        if (!selectedApproval) return
        await executeApprove(selectedApproval.uuid)
    }

    const { execute: executeReject, status: rejectStatus } = useCommand({
        commandFn: async (data: { approvalEntityUUID: string, rejectionDescription: string }) => {
            return await ApiClient.rejectApproval(
                data.approvalEntityUUID,
                data.rejectionDescription
            )
        },
        onFinish: result => {
            if (result.status === 'success' && result.response) {
                snackbar.show({
                    severity: 'info',
                    text: `Approval rejected for ${selectedApproval?.entityName}`
                })

                invalidateApprovalHub()
                invalidateEntityApprovalStatus()

                if (selectedApproval.entityType === 'contract') {
                    invalidateContractListSmartTable()
                    invalidateContractData()
                    invalidateContractsVersion()
                }

                if (selectedApproval.entityType === 'schema') {
                    invalidateContractSchemaList()
                    invalidateSchemaData()
                    invalidateSchemaVersions()
                }

                if (selectedApproval.entityType === 'flow') {
                    invalidateFlowSettlementApprovals()
                    invalidateFlowVersions()
                    invalidateFlow()
                    invalidateApprovedEngines()
                    invalidatePendingEngines()
                }

                if (selectedApproval.entityType === 'settlement') {
                    invalidateFlowSettlementApprovals()
                    invalidateSettlementsReport()
                    invalidateSettlementDetails()
                    invalidateSettlements()
                }
                handleCloseModal()
            } else if (result.status === 'error') {
                console.error('Error rejecting request:', result.errorText)
                snackbar.show({
                    severity: 'error',
                    text: 'Failed to approve request: ' + result.errorText
                })
            }
        }
    })

    const handleReject = async (rejectionReason: string) => {
        if (!selectedApproval || !rejectionReason.trim()) return

        await executeReject({
            approvalEntityUUID: selectedApproval.uuid,
            rejectionDescription: rejectionReason.trim()
        })
    }

    const handleRowClick = async (indexRow: number, cells: Cell[], row: Row, item: IApprovalRequest) => {
        switch (item.entityType) {
            case 'contract':
                navigateToContract(item)
                break
            case 'schema':
                dispatch(
                    changeRoute({
                        route: 'contract-schema-editor',
                        params: { uuid: item.approvalEntityUUID }
                    })
                )
                break
            case 'flow':
                dispatch(
                    changeRoute({
                        route: 'flow',
                        params: { flowId: item.approvalEntityUUID }
                    })
                )
                break
            case 'settlement':
                navigateToSettlement(item)
                break
            default:
                console.warn(`Unknown entityType: ${item.entityType}`)
                break
        }
    }

    const isLoading = navigateToContractStatus === 'loading' || navigateToSettlementStatus === 'loading'

    return (
        <Tile.Container>
            {isLoading && (
                <Box
                    position='fixed'
                    top={0}
                    left={0}
                    width='100%'
                    height='100%'
                    bgcolor='rgba(0, 0, 0, 0.3)'
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                    zIndex={9999}>
                    <CircularProgress size={60} />
                </Box>
            )}

            <Stack spacing={3}>
                <Stack justifyContent='space-between' spacing={1}>
                    <Typography variant='h5'>
                        <strong>Approvals</strong>
                    </Typography>
                    <Typography>
                        Manage all approval requests for new contract, report...
                    </Typography>
                </Stack>

                <ApprovalTable
                    onManageApproval={handleManageApproval}
                    onRowClick={handleRowClick}
                    approvalData={response}
                    isLoading={status}

                />

                <ApprovalModal
                    open={openModal}
                    onClose={handleCloseModal}
                    approval={selectedApproval}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    rejectStatus={rejectStatus}
                    approveStatus={approveStatus}
                />
            </Stack>
        </Tile.Container>
    )
}

export default Approvals
